export type RenderPart =
    | "app"
    | "map"
    | "clock"
    | "shade"
    | "contour"
    | "wind"
    | "arrow"
    | "particle"
    | "export"
    | "video"
    | "zip";

export interface RenderStatus {
    ready: boolean;
    generation: number;
    pending: RenderPart[];
    errors: Partial<Record<RenderPart, string>>;
}

export class RenderCoordinator extends EventTarget {
    private readonly generations = new Map<RenderPart, number>();
    private readonly pending = new Set<RenderPart>();
    private readonly errors = new Map<RenderPart, Error>();
    private generation = 0;

    track<T>(part: RenderPart, task: Promise<T> | T): Promise<T> {
        const partGeneration = (this.generations.get(part) || 0) + 1;
        this.generations.set(part, partGeneration);
        this.generation++;
        this.pending.add(part);
        this.errors.delete(part);
        this.changed();
        return Promise.resolve(task).then(
            (result) => {
                this.finish(part, partGeneration);
                return result;
            },
            (reason: unknown) => {
                const error =
                    reason instanceof Error
                        ? reason
                        : new Error(String(reason));
                if (this.generations.get(part) === partGeneration) {
                    this.errors.set(part, error);
                }
                this.finish(part, partGeneration);
                throw error;
            },
        );
    }

    status(): RenderStatus {
        return {
            ready: this.pending.size === 0 && this.errors.size === 0,
            generation: this.generation,
            pending: [...this.pending],
            errors: Object.fromEntries(
                [...this.errors].map(([part, error]) => [part, error.message]),
            ),
        };
    }

    async wait(timeoutMs = 60000) {
        const started = performance.now();
        while (this.pending.size > 0) {
            const remaining = timeoutMs - (performance.now() - started);
            if (remaining <= 0) {
                throw new Error(
                    `Rendering timed out: ${[...this.pending].join(", ")}`,
                );
            }
            await new Promise<void>((resolve, reject) => {
                const timeout = window.setTimeout(
                    () => reject(new Error("Rendering timed out")),
                    remaining,
                );
                this.addEventListener(
                    "change",
                    () => {
                        window.clearTimeout(timeout);
                        resolve();
                    },
                    { once: true },
                );
            });
        }
        if (this.errors.size > 0) {
            const detail = [...this.errors]
                .map(([part, error]) => `${part}: ${error.message}`)
                .join("; ");
            throw new Error(`Rendering failed: ${detail}`);
        }
        await new Promise<void>((resolve) =>
            requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
        );
        return this.status();
    }

    clearError(part: RenderPart) {
        this.errors.delete(part);
        this.changed();
    }

    private finish(part: RenderPart, generation: number) {
        if (this.generations.get(part) === generation) {
            this.pending.delete(part);
        }
        this.changed();
    }

    private changed() {
        this.dispatchEvent(new Event("change"));
    }
}
